import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import Layout from '../component/layout';
import { makeRequest2 } from '../../../utils/makeRequest';
import { getFacilitiesURL } from '../../../utils/urls';

// 👉 The switches / gateways you want to monitor (by name)
const MONITORED_TARGETS = [
  {
    id: 'gw1',
    name: 'Payserve test',
    ip: '192.168.99.1',
    port: 80,
  },
  {
    id: 'sw1',
    name: 'Main Switch - Head Office',
    ip: '192.168.0.11',
    port: 80,
  },
];

// Badge colours for status
const statusColors = {
  Online: 'bg-success',
  Offline: 'bg-danger',
  Unknown: 'bg-secondary',
};

const getStatusClass = (status) =>
  statusColors[status] || statusColors.Unknown;

function NetworkMonitorDashboard() {
  // statusById: { [id]: { status, latencyMs, lastCheckedAt } }
  const [statusById, setStatusById] = useState({});
  const [isPingingAll, setIsPingingAll] = useState(false);

  // Facilities + facility filter
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(''); // we expect a string

  const pingEndpoint = `http://localhost:4747/api/network/ping`;

  // 👉 Fetch facilities for the dropdown (show ALL facilities)
  const fetchFacilities = async () => {
    try {
      const facilitiesRes = await makeRequest2(getFacilitiesURL, 'GET');

      if (facilitiesRes && facilitiesRes.success) {
        let facilitiesData = [];

        if (Array.isArray(facilitiesRes.data)) {
          facilitiesData = facilitiesRes.data;
        } else if (
          facilitiesRes.data &&
          Array.isArray(facilitiesRes.data.facilities)
        ) {
          facilitiesData = facilitiesRes.data.facilities;
        } else if (
          facilitiesRes.facilities &&
          Array.isArray(facilitiesRes.facilities)
        ) {
          facilitiesData = facilitiesRes.facilities;
        }

        setFacilities(facilitiesData);
      } else {
        setFacilities([]);
      }
    } catch (err) {
      console.error('Error fetching facilities:', err);
      setFacilities([]);
    }
  };

  // Ping a single target
  const pingTarget = async (target) => {
    try {
      const res = await fetch(pingEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip: target.ip, port: target.port }),
      });

      const data = await res.json().catch(() => ({}));

      const online = res.ok && data?.online;
      return {
        status: online ? 'Online' : 'Offline',
        latencyMs: typeof data?.latencyMs === 'number' ? data.latencyMs : null,
        lastCheckedAt: new Date().toISOString(),
      };
    } catch (err) {
      console.error('Ping error for', target.name, err);
      return {
        status: 'Offline',
        latencyMs: null,
        lastCheckedAt: new Date().toISOString(),
      };
    }
  };

  // Ping all targets
  const pingAllTargets = async () => {
    setIsPingingAll(true);
    const results = await Promise.all(
      MONITORED_TARGETS.map(async (t) => {
        const result = await pingTarget(t);
        return [t.id, result];
      })
    );

    setStatusById((prev) => {
      const next = { ...prev };
      results.forEach(([id, value]) => {
        next[id] = value;
      });
      return next;
    });

    setIsPingingAll(false);
  };

  // On mount: fetch facilities + ping all, then ping every 30s
  useEffect(() => {
    fetchFacilities();
    pingAllTargets();

    const intervalId = setInterval(() => {
      pingAllTargets();
    }, 30000); // 30 seconds

    return () => clearInterval(intervalId);
  }, []); // MONITORED_TARGETS is constant

  // Manual ping for a single card
  const handlePingSingle = async (target) => {
    const result = await pingTarget(target);
    setStatusById((prev) => ({
      ...prev,
      [target.id]: result,
    }));
  };

  const formatLastChecked = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString();
  };

  const formatLatency = (latencyMs) => {
    if (latencyMs == null) return '—';
    return `${latencyMs} ms`;
  };

  // 👉 Filter by facility: check if device name *includes* facility name (partial match)
  const filteredTargets = MONITORED_TARGETS.filter((t) => {
    // ensure we only call trim on strings
    const rawValue =
      typeof selectedFacility === 'string' ? selectedFacility : '';

    const selectedName = rawValue.trim().toLowerCase();

    // "All Facilities" → show everything
    if (!selectedName) return true;

    const deviceName = (t.name || '').toLowerCase();

    return deviceName.includes(selectedName); // partial match, not equality
  });

  return (
    <Layout>
      <div className="page-header">
        <ul className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/core/dashboard">Dashboard</Link>
          </li>
          <li className="breadcrumb-item">Network Monitoring</li>
          <li className="breadcrumb-item active">Switches & Gateways</li>
        </ul>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Switch & Gateway Health</h4>

              <div className="d-flex gap-2 align-items-center">
                {/* Facility Filter Dropdown (scrollable, shows all facilities) */}
                <Dropdown
                  value={selectedFacility}
                  onChange={(e) => setSelectedFacility(e.value)}
                  options={[
                    { label: 'All Facilities', value: '' },
                    ...facilities.map((f) => ({
                      label: f.name,
                      value: f.name, // we keep it as a string
                    })),
                  ]}
                  placeholder="Filter by facility"
                  filter
                  className="w-100"
                  panelStyle={{ maxHeight: '250px', overflowY: 'auto' }}
                  style={{ minWidth: 220 }}
                />

                <Button
                  label={isPingingAll ? 'Refreshing...' : 'Refresh All'}
                  icon="ti ti-refresh"
                  onClick={pingAllTargets}
                  disabled={isPingingAll}
                />
              </div>
            </div>

            <div className="card-body">
              <div className="row">
                {filteredTargets.map((t) => {
                  const s = statusById[t.id] || {
                    status: 'Unknown',
                    latencyMs: null,
                    lastCheckedAt: null,
                  };

                  return (
                    <div key={t.id} className="col-md-4 col-sm-6 mb-3">
                      <div className="card h-100 shadow-sm">
                        <div className="card-body">
                          {/* Header: name + status badge */}
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <h5 className="card-title mb-0">
                              {t.name || 'Device'}
                            </h5>
                            <span
                              className={`badge ${getStatusClass(s.status)}`}
                            >
                              {s.status}
                            </span>
                          </div>

                          {/* Icon */}
                          <div className="mb-3">
                            <i
                              className="ti ti-server"
                              style={{ fontSize: 24 }}
                            />
                          </div>

                          {/* Details */}
                          <p className="mb-1">
                            <strong>IP:</strong> {t.ip}
                          </p>
                          <p className="mb-1">
                            <strong>Port:</strong> {t.port}
                          </p>
                          <p className="mb-1">
                            <strong>Latency:</strong>{' '}
                            {formatLatency(s.latencyMs)}
                          </p>
                          <p className="mb-1">
                            <strong>Last Checked:</strong>{' '}
                            {formatLastChecked(s.lastCheckedAt)}
                          </p>

                          {/* Actions */}
                          <div className="d-flex justify-content-end mt-3">
                            <Button
                              label="Check now"
                              icon="ti ti-radar"
                              className="p-button-rounded p-button-info"
                              onClick={() => handlePingSingle(t)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className="text-muted mt-3">
                This view auto-refreshes every <strong>30 seconds</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default NetworkMonitorDashboard;
